import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { Kicker, Reveal, SectionTitle, staggerContainer, staggerItem } from "@/components/Reveal";
import SplitText from "@/components/motion/SplitText";
import Counter from "@/components/motion/Counter";
import ImageReveal from "@/components/motion/ImageReveal";
import Button from "@/components/Button";
import { BrandIcon } from "@/components/BrandIcon";
import EditorialMarquee from "@/components/Marquee";
import CommunitySection from "@/components/CommunitySection";
import YearSection from "@/components/YearSection";
import JoinCta from "@/components/JoinCta";
import EventsSection from "@/components/EventsSection";
import SpotlightSection from "@/components/SpotlightSection";

const EASE = [0.22, 1, 0.36, 1];

function Hero({ c }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const markY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const lines = c.lines("hero", "title_lines");

  return (
    <section
      ref={ref}
      className="relative overflow-hidden border-b border-line bg-sand pt-[72px] md:pt-20"
      style={{
        backgroundImage: "radial-gradient(60% 70% at 85% 30%, rgba(230,240,234,0.95), transparent 70%)",
      }}
      data-testid="hero-section"
    >
      <div className="grid-bg pointer-events-none absolute inset-0" aria-hidden />
      <div className="relative mx-auto grid max-w-[1200px] items-center gap-12 px-5 pb-16 pt-14 md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] md:gap-14 md:pb-24 md:pt-20">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            data-testid="hero-kicker"
          >
            <Kicker>{c.get("hero", "kicker")}</Kicker>
          </motion.div>
          <h1
            className="mt-4 font-display text-[2.3rem] font-semibold leading-[1.1] tracking-[-0.01em] text-ink sm:text-5xl lg:text-[3.9rem]"
            data-testid="hero-title"
          >
            {lines.map((line, i) => (
              <SplitText
                key={i}
                text={line}
                as="span"
                className={`block ${i === 1 ? "text-pine" : ""}`}
                delay={0.4 + i * 0.25}
                stagger={0.07}
                duration={1}
              />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-6 max-w-[62ch] text-lg leading-relaxed text-body md:text-xl"
            data-testid="hero-subtitle"
          >
            {c.get("hero", "subtitle")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Button to="/membership" variant="primary" data-testid="hero-cta-member">
              {c.get("hero", "primary_cta")}
            </Button>
            <Button to="/contact" variant="outline" arrow="up" data-testid="hero-cta-work">
              {c.get("hero", "secondary_cta")}
            </Button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.7 }}
            className="mt-6 max-w-md text-[0.95rem] text-muted"
            data-testid="hero-caption"
          >
            {c.get("hero", "image_caption")}
          </motion.p>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0 round 10px)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0 round 10px)" }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="relative aspect-[4/5] overflow-hidden rounded-[10px] shadow-[0_30px_60px_-30px_rgba(18,33,27,0.35)] md:aspect-[5/6]"
          >
            <motion.img
              src={c.get("hero", "image", "/images/hero.jpg")}
              alt="Educators in discussion at Veridian"
              style={{ y: imgY, scale: 1.15 }}
              className="img-editorial h-full w-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-pine/50 via-transparent to-transparent" />
          </motion.div>
          <motion.img
            src="/brand/mark.png"
            alt=""
            aria-hidden
            style={{ y: markY }}
            initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.4, delay: 1.2, ease: EASE }}
            className="float-slow absolute -bottom-10 -left-8 w-32 drop-shadow-[0_20px_30px_rgba(18,33,27,0.25)] md:-left-16 md:w-48"
          />
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.9 }}
            className="absolute right-4 top-4 flex items-center gap-2 rounded-md bg-white/90 px-3 py-1.5 font-display text-xs font-semibold text-pine shadow-sm backdrop-blur"
            data-testid="hero-scroll-hint"
          >
            Beginning in Pune
            <motion.span animate={{ y: [0, 3, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
              <ArrowDown className="h-3.5 w-3.5" />
            </motion.span>
          </motion.span>
        </div>
      </div>
    </section>
  );
}

function Stats({ c }) {
  const items = c.pairs("stats", "items");
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-2 gap-5 md:grid-cols-4" data-testid="stats-strip">
      {items.map(([num, label, sub], i) => (
        <Reveal key={i} delay={i * 0.08} className="border-t-2 border-terra pt-4" data-testid={`stat-${i}`}>
          <Counter to={num} className="font-display text-5xl font-semibold text-pine md:text-6xl" />
          <p className="mt-2 font-display text-lg font-medium text-ink">{label}</p>
          <p className="mt-0.5 text-sm text-muted">{sub}</p>
        </Reveal>
      ))}
    </div>
  );
}

export default function Home() {
  const c = useContent();
  const offerings = c.pairs("offerings", "items").slice(0, 3);
  const spans = [
    "md:col-span-7 aspect-[16/10]",
    "md:col-span-5 aspect-[4/5] md:aspect-auto",
    "md:col-span-12 aspect-[21/9]",
  ];

  return (
    <div data-testid="home-page">
      <Hero c={c} />
      <EditorialMarquee dark={false} />

      {/* WHY */}
      <section className="relative overflow-hidden bg-bone" data-testid="why-section">
        <div className="mx-auto max-w-[1200px] px-5 py-20 md:py-28">
          <div className="grid gap-14 md:grid-cols-12">
            <div className="md:col-span-5">
              <Reveal>
                <Kicker>{c.get("why", "kicker")}</Kicker>
              </Reveal>
              <SplitText
                as="h2"
                inView
                text={c.get("why", "title")}
                className="mt-4 font-display text-3xl font-semibold leading-[1.15] text-ink md:text-[2.4rem]"
              />
              <Reveal delay={0.3}>
                <p
                  className="mt-8 rounded-r-[10px] border-l-[5px] border-pine bg-surface px-7 py-6 font-display text-xl font-medium leading-snug text-ink md:text-2xl"
                  data-testid="why-closing"
                >
                  {c.get("why", "closing")}
                </p>
              </Reveal>
            </div>
            <motion.ul
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="grid gap-5 sm:grid-cols-2 md:col-span-7"
            >
              {c.pairs("why", "points").map(([p, icon], i) => (
                <motion.li
                  key={i}
                  variants={staggerItem}
                  className="group relative overflow-hidden rounded-[10px] border border-line bg-surface p-7 shadow-[0_10px_30px_rgba(18,33,27,0.04)] transition-[background-color,transform,box-shadow] duration-700 hover:-translate-y-1 hover:bg-pine hover:shadow-[0_20px_40px_rgba(18,33,27,0.12)]"
                  data-testid={`why-point-${i}`}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-bone text-pine transition-colors duration-700 group-hover:bg-white/10 group-hover:text-terra">
                    <BrandIcon name={icon} className="h-5 w-5" />
                  </span>
                  <p className="mt-8 font-display text-xl font-medium leading-snug text-ink transition-colors duration-700 group-hover:text-white">
                    {p}
                  </p>
                  <span className="absolute right-6 top-6 font-display text-sm text-terra">0{i + 1}</span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
          <div className="mt-16 md:mt-20">
            <Stats c={c} />
          </div>
        </div>
      </section>

      <CommunitySection />
      <SpotlightSection tint />

      {/* OFFERINGS PREVIEW */}
      <section className="bg-surface" data-testid="offerings-preview">
        <div className="mx-auto max-w-[1200px] px-5 py-20 md:py-28">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-3xl">
              <Kicker>{c.get("offerings", "kicker")}</Kicker>
              <SectionTitle>{c.get("offerings", "title")}</SectionTitle>
            </div>
            <Button to="/programs" variant="ghost" arrow="up" data-testid="offerings-view-all">
              All programmes
            </Button>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
            {offerings.map(([title, desc, img, icon], i) => (
              <Link
                to="/programs"
                key={i}
                className={`group relative block overflow-hidden rounded-[10px] ${spans[i]} ${i === 1 ? "md:min-h-[420px]" : ""}`}
                data-testid={`offering-card-${i}`}
                data-cursor="view"
              >
                <ImageReveal
                  src={img || "/images/texture.jpg"}
                  alt={title}
                  className="absolute inset-0 h-full"
                  delay={i * 0.1}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent transition-opacity duration-700 group-hover:opacity-95" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 p-7 text-sand md:p-9">
                  <div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-white/15 text-terra backdrop-blur">
                      <BrandIcon name={icon} className="h-4 w-4" />
                    </span>
                    <h3 className="mt-5 font-display text-2xl font-semibold text-white">{title}</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-sand/75 md:max-h-0 md:translate-y-2 md:opacity-0 md:transition-all md:duration-700 md:group-hover:max-h-24 md:group-hover:translate-y-0 md:group-hover:opacity-100">
                      {desc}
                    </p>
                  </div>
                  <ArrowUpRight className="h-6 w-6 shrink-0 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <YearSection />
      <EventsSection limit={3} showAllLink />

      {/* DIFFERENT */}
      <section className="bg-bone" data-testid="different-section">
        <div className="mx-auto max-w-[1200px] px-5 py-20 md:py-28">
          <Reveal className="max-w-3xl">
            <Kicker>{c.get("different", "kicker")}</Kicker>
            <SectionTitle>{c.get("different", "title")}</SectionTitle>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-12 border-t border-line"
          >
            {c.pairs("different", "items").map(([title, desc], i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                className="group relative grid grid-cols-1 gap-3 border-b border-line py-6 md:grid-cols-12 md:items-baseline md:gap-8 md:px-4"
                data-testid={`different-item-${i}`}
              >
                <span
                  className="absolute inset-y-0 left-0 w-0 rounded-[10px] bg-surface transition-[width] duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:w-full"
                  aria-hidden
                />
                <span className="relative font-display text-lg text-terra md:col-span-1">0{i + 1}</span>
                <h3 className="relative font-display text-xl font-semibold text-ink md:col-span-4">{title}</h3>
                <p className="relative leading-relaxed text-body md:col-span-6">{desc}</p>
                <ArrowUpRight className="relative hidden h-5 w-5 text-pine opacity-0 transition-all duration-500 group-hover:opacity-100 md:col-span-1 md:block md:justify-self-end" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <JoinCta />
    </div>
  );
}
